/**
 * Represents a state transition in the execution lifecycle
 *
 * @class StateTransition
 * @property {string} fromState - The state before the transition
 * @property {string} toState - The state after the transition
 * @property {Date} timestamp - When the transition occurred
 * @property {string} userId - User who triggered the transition
 * @property {string|null} reason - Optional note (e.g. stall justification)
 */

class StateTransition {
  constructor(fromState, toState, timestamp, userId, reason = null, id = null) {
    this.fromState = fromState;
    this.toState = toState;
    this.timestamp = timestamp;
    this.userId = userId;
    this.reason = reason;
    this.id = id;
  }

  /**
   * Get timestamp as Date object
   * @returns {Date} Timestamp as Date
   */
  getTimestamp() {
    if (this.timestamp?.toDate) {
      return this.timestamp.toDate();
    }
    if (this.timestamp instanceof Date) {
      return this.timestamp;
    }
    return new Date(this.timestamp);
  }

  /**
   * Create StateTransition instance from Firestore document data
   * @param {Object} data - Firestore document data
   * @returns {StateTransition} New StateTransition instance
   */
  static fromFirestore(data, id = null) {
    const rawReason = data.reason != null ? String(data.reason).trim() : '';
    return new StateTransition(
      data.fromState || null,
      data.toState || null,
      data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp,
      data.userId || data.uid || null,
      rawReason || null,
      id,
    );
  }

  /**
   * Create multiple StateTransition instances from array
   * @param {Array} dataArray - Array of transition data
   * @returns {Array<StateTransition>} Array of StateTransition instances
   */
  static fromFirestoreArray(dataArray = []) {
    if (!Array.isArray(dataArray)) {
      return [];
    }
    return dataArray.map(data => StateTransition.fromFirestore(data));
  }
}

export default StateTransition;
